import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  Link,
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { ForgotPasswordFormData } from '../../types/auth';
import { forgotPasswordSchema } from '../../utils/validation';
import { useAuth } from '../../hooks/useAuth';

const ForgotPasswordPage: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { resetPassword, error: authError, clearError } = useAuth();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(forgotPasswordSchema) as any,
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  const watchedEmail = watch('email');

  const onSubmit = async (data: ForgotPasswordFormData) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    clearError();

    try {
      await resetPassword(data.email);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Forgot password error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/auth/login');
  };

  const handleResendEmail = async () => {
    if (watchedEmail) {
      setIsSubmitting(true);
      try {
        await resetPassword(watchedEmail);
      } catch (error) {
        console.error('Resend email error:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (isSubmitted) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: { xs: '100%', sm: '480px', md: '520px', lg: '560px' },
            width: '100%',
          }}
        >
          <Card
            elevation={8}
            sx={{
              borderRadius: { xs: 2, sm: 3, md: 4 },
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              width: '100%',
              maxWidth: '100%',
            }}
          >
            <CardContent
              sx={{ p: { xs: 3, sm: 4, md: 5, lg: 6 }, textAlign: 'center' }}
            >
              {/* Success Icon */}
              <CheckCircleIcon
                sx={{
                  fontSize: 64,
                  color: 'success.main',
                  mb: 2,
                }}
              />

              {/* Header */}
              <Typography variant="h5" component="h1" gutterBottom>
                メールを送信しました
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                パスワードリセットのリンクを以下のメールアドレスに送信しました：
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'medium',
                  color: 'primary.main',
                  mb: 3,
                }}
              >
                {watchedEmail}
              </Typography>

              {/* Instructions */}
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>次の手順：</strong>
                  <br />
                  1. メールボックスを確認してください
                  <br />
                  2. 「パスワードリセット」のメールを開いてください
                  <br />
                  3.
                  メール内のリンクをクリックしてパスワードを再設定してください
                  <br />
                  <br />
                  メールが見つからない場合は、迷惑メールフォルダもご確認ください。
                </Typography>
              </Alert>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleResendEmail}
                  disabled={isSubmitting}
                  sx={{ py: 1.5 }}
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      再送信中...
                    </>
                  ) : (
                    'メールを再送信'
                  )}
                </Button>

                <Button
                  variant="contained"
                  onClick={handleBackToLogin}
                  startIcon={<ArrowBackIcon />}
                  sx={{ py: 1.5 }}
                >
                  ログインページに戻る
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: { xs: '100%', sm: '480px', md: '520px', lg: '560px' },
          width: '100%',
        }}
      >
        <Card
          elevation={8}
          sx={{
            borderRadius: { xs: 2, sm: 3, md: 4 },
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 5, lg: 6 } }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #667eea, #764ba2)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                パスワードを忘れた場合
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                パスワードリセット用のリンクをメールアドレスに送信します
              </Typography>
            </Box>

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit(onSubmit as any)}>
              {/* Error Alert */}
              {authError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {authError}
                </Alert>
              )}

              {/* Email Field */}
              <TextField
                {...register('email')}
                fullWidth
                label="メールアドレス"
                type="email"
                autoComplete="email"
                margin="normal"
                error={!!errors.email}
                helperText={errors.email?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSubmitting}
                sx={{
                  mb: 2,
                  py: 1.5,
                  position: 'relative',
                }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress
                      size={20}
                      sx={{
                        position: 'absolute',
                        left: '50%',
                        marginLeft: '-10px',
                      }}
                    />
                    送信中...
                  </>
                ) : (
                  'リセットリンクを送信'
                )}
              </Button>

              {/* Back to Login Link */}
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Link
                  component={RouterLink}
                  to="/auth/login"
                  sx={{
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                  ログインページに戻る
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: { xs: 2, sm: 3, md: 4 } }}>
          <Typography
            variant="body2"
            color="white"
            sx={{
              opacity: 0.8,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          >
            © {currentYear} ガクフドキャリア. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPasswordPage;
